import {Indicator, Node} from "@/types/models";
import {createContext, PropsWithChildren, useContext, useEffect, useState} from "react";
import {useSessionMetadataContext} from "@/Context/SessionMetadataContext";
import {useNodeNavigationContext} from "@/Context/NodeNavigationContext";
import {useProgressContext} from "@/Context/ProgressContext";
import {useResponseContext} from "@/Context/ResponseContext";
import {createArrayNode, deleteArrayNode, flattenHierarchy} from "@/services/NodeService";
import {toast} from "react-toastify";
import Modal from "@/Components/Modal";
import SecondaryButton from "@/Components/SecondaryButton";
import DangerButton from "@/Components/DangerButton";

type RemovingArrayNode = {
    node: Node
    setChildren?: (children: Node[]) => void
    confirmed?: boolean
    resolve: (value: Node | undefined) => void
    reject: (error: Error) => void
}

type NodeArrayContextProps = {
    activeArrayNode: Node | undefined
    addArrayNode: (node: Node, setChildren?: (children: Node[]) => void) => Promise<Node>
    removeArrayNode: (node: Node, setChildren?: (children: Node[]) => void, confirmed?: boolean) => void
    getArrayNode: (node: Node) => Node | undefined
}

const NodeArrayContext = createContext<NodeArrayContextProps | undefined>(undefined)

export const NodeArrayContextProvider = ({children}: PropsWithChildren) => {
    const {session, rootNode, setRootNode, nodeTypes} = useSessionMetadataContext()
    const {
        activeNode,
        setActiveNode,
        nodes,
        setNodes,
        previousNode,
        nextNode,
        findNode,
        findIndicator,
        updateChildren,
        linkChildren,
        updateNodesText
    } = useNodeNavigationContext()
    const {loading, setLoading} = useProgressContext()
    const {respond} = useResponseContext()

    const [activeArrayNode, setActiveArrayNode] = useState<Node | undefined>(undefined)
    const [removingArrayNode, setRemovingArrayNode] = useState<RemovingArrayNode | undefined>()

    const getArrayNode = (node: Node): Node | undefined => {
        if (node.array_index) return findNode(node.id, node.array_index)

        while (node.parent) {
            if (node.parent.array_index) {
                node.parent = findNode(node.parent_id!, node.parent.array_index)
                return node.parent
            }
            node = node.parent
        }

        return undefined
    }

    const fixNodeText = (node: Node | null): void => {
        if (!node) return

        const findArrayIndex = (node: Node): number | undefined => {
            let current: Node | undefined = node
            while (current && current.array_index === undefined) {
                current = current.parent
            }

            return current?.array_index
        }

        let index = node.array_index
        if (index === undefined) index = findArrayIndex(node)
        if (!index) return

        const idx = `${index}`
        node.title = (node.xtitle ?? node.title).replace("~array_index~", idx)
        node.name = (node.xname ?? node.name).replace("~array_index~", idx)
        if (node.description) node.description = (node.xdescription ?? node.description).replace("~array_index~", idx)
    }

    const addArrayNode = async (node: Node, setChildren?: (children: Node[]) => void): Promise<Node> => {
        setLoading(true)
        if (!node.parent) node.parent = findNode(node.parent_id!)
        const lastIndex = node.parent!.children!.findLastIndex(c => c.id === node.id)

        const promise: Promise<Node> = createArrayNode(node, session!)
        promise.then((added: Node) => {
            added.parent = node!.parent

            const siblings = node.parent!.children!.filter(c => c.id === node.id && ((c.array_index ?? 0) > 0))
            const arrayCount = siblings.length + 1

            if (arrayCount === 1 && node.parent!.children!.length === 1) {
                node.parent!.children!.splice(0, 1)
            }

            // IMPORTANT: make the same instance consistent, so later lookups use the right array_index.
            added.array_index = arrayCount
            added.array_count = arrayCount

            node!.parent!.children!.splice(lastIndex + 1, 0, added)

            let i = 0
            for (const sibling of siblings) {
                sibling.array_count = arrayCount
                sibling.array_index = ++i
            }

            updateChildren(added)
            updateNodesText(nodes)

            const nextRoot = {...rootNode!}
            setRootNode(nextRoot)
            setNodes({type: "SET", nodes: flattenHierarchy([nextRoot])})

            if (arrayCount > 1) {
                const indicators = flattenHierarchy([added]).map(n => n.content).flat().filter(
                    c => c._class === 'Indicator' && !!c.assume_from_indicator_id
                ) as Indicator[]
                for (const indicator of indicators) {
                    const source = findIndicator(indicator.assume_from_indicator_id!, siblings[arrayCount - 2])
                    if (source && source.response) {
                        const asText = !!source.response.input_text
                        const input = asText ? `${source.response.input_text}` : `${source.response.input}`
                        respond(indicator, arrayCount, input, asText, source.response.id)
                    }
                }
            }

            setLoading(false)

            setTimeout(() => {
                const toActivate = findNode(added.id, added.array_index) ?? added
                setActiveNode(toActivate)
            }, 0)
        })

        return promise
    }

    const removeArrayNode = (node: Node, setChildren?: (children: Node[]) => void, confirmed?: boolean) => {
        if (!confirmed) {
            return new Promise<Node | undefined>((resolve, reject) => {
                setRemovingArrayNode({
                    node: node,
                    setChildren: setChildren,
                    confirmed: !!node.metadata?.allow_delete_without_prompt,
                    resolve: resolve,
                    reject: reject
                })
            })
        }
        setLoading(true)

        const promise: Promise<Node> = deleteArrayNode(node, session!)
        promise.then((deleted: Node) => {
            if (node === activeNode) setActiveNode(previousNode ?? nextNode ?? rootNode)
            const index = node.parent!.children!.findIndex(c => c.id === node.id && c.array_index === node.array_index)

            node.parent!.children!.splice(index, 1)
            const allNodes = nodes.filter(n => n.id !== node.id || n.array_index !== node.array_index)
            setNodes({type: "SET", nodes: allNodes})

            const siblings = node.parent!.children!.filter(c => c.id === node.id)
            if (!siblings.length) {
                node.array_count = 0
                node.array_index = 0
                fixNodeText(node)
                node.parent!.children!.splice(index, 0, node)
            } else {
                const arrayCount = siblings.length
                let i = 0
                for (const sibling of siblings) {
                    sibling.array_count = arrayCount
                    sibling.array_index = ++i
                    fixNodeText(sibling)
                }
            }

            if (activeArrayNode?.id === node.id) {
                const toActivate = siblings.length ? siblings[siblings.length - 1] : node.parent!
                setActiveNode(toActivate)
            }

            removingArrayNode!.resolve(node)
            const flatNodes = flattenHierarchy(nodes)
            updateNodesText(flatNodes)
            setRootNode({...rootNode!})
            toast.info(`${node.title} has been removed.`)
        }).catch((error: Error) => {
            removingArrayNode!.reject(error)
            toast.error(`${node.title} cannot be removed.`)
            console.error(error)
        }).finally(() => {
            setRemovingArrayNode(undefined)
            setLoading(false)
        })

        return promise
    }

    useEffect(() => {
        if (removingArrayNode && removingArrayNode.confirmed) {
            removeArrayNode(removingArrayNode.node, removingArrayNode.setChildren, true)
        }
    }, [removingArrayNode])

    useEffect(() => {
        const arrayNode = activeNode ? getArrayNode(activeNode) : undefined
        setActiveArrayNode(arrayNode)
    }, [activeNode])

    return <NodeArrayContext.Provider value={{
        activeArrayNode,
        addArrayNode,
        removeArrayNode,
        getArrayNode,
    }}>
        {children}

        <Modal show={removingArrayNode !== undefined && !removingArrayNode.confirmed}
               onClose={() => setRemovingArrayNode(undefined)}>
            <div className="p-6">
                <h2 className="text-lg font-medium text-heading dark:text-heading-dark">
                    Delete {removingArrayNode?.node.title}?
                </h2>

                <p className="mt-1 text-sm text-text">
                    Are you sure you want to delete all responses and data for
                    {" "}{removingArrayNode?.node.title ?? "this item"}{removingArrayNode?.node.array_index ? ` ${removingArrayNode.node.array_index} of  ${removingArrayNode.node.array_count}` : ''}?
                </p>

                <div className="mt-6 flex justify-end">
                    <SecondaryButton onClick={() => setRemovingArrayNode(undefined)}>Cancel</SecondaryButton>

                    <DangerButton className="ms-3" disabled={loading}
                                  onClick={() => setRemovingArrayNode({...removingArrayNode!, confirmed: true})}>
                        Delete {nodeTypes?.find(t => t.id === removingArrayNode?.node.node_type_id!)?.name}
                    </DangerButton>
                </div>
            </div>
        </Modal>
    </NodeArrayContext.Provider>
}

export function useNodeArrayContext(): NodeArrayContextProps {
    const context = useContext(NodeArrayContext);
    if (!context) throw new Error("No NodeArrayContext is defined")
    return context
}
